import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useField} from 'formik'
import {Form, Nav} from 'react-bootstrap'
import AsyncSelect from 'react-select/async'
import api from '../../../api'

type TComponentJsonBuilder = {
    article: string
    is_disabled?: boolean
    defaultJson?: string
}

type TSelectOption = {title: string; value: number}

const safeParse = (v: any) => {
    if (!v) return null
    if (typeof v === 'object') return v
    if (typeof v !== 'string') return null
    try {
        return JSON.parse(v)
    } catch {
        return null
    }
}

const pretty = (obj: any) => {
    try {
        return JSON.stringify(obj ?? {}, null, 2)
    } catch {
        return '{}'
    }
}

const isPlainObject = (v: any) => v && typeof v === 'object' && !Array.isArray(v)

const guessType = (v: any): 'string' | 'number' | 'boolean' | 'array' | 'object' | 'null' => {
    if (v === null || v === undefined) return 'null'
    if (Array.isArray(v)) return 'array'
    if (typeof v === 'boolean') return 'boolean'
    if (typeof v === 'number') return 'number'
    if (typeof v === 'string') return 'string'
    if (isPlainObject(v)) return 'object'
    return 'string'
}

const setByPath = (root: any, path: string, nextValue: any) => {
    const parts = path
        .replace(/\[(\d+)\]/g, '.$1')
        .split('.')
        .filter(Boolean)

    const clone = Array.isArray(root) ? [...root] : {...(root ?? {})}
    let cur: any = clone

    for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i]
        const nxt = parts[i + 1]
        if (cur[p] == null) cur[p] = /^\d+$/.test(nxt) ? [] : {}
        cur[p] = Array.isArray(cur[p]) ? [...cur[p]] : {...cur[p]}
        cur = cur[p]
    }

    cur[parts[parts.length - 1]] = nextValue
    return clone
}

const getFromPath = (root: any, path: string) => {
    const parts = path
        .replace(/\[(\d+)\]/g, '.$1')
        .split('.')
        .filter(Boolean)
    let cur = root
    for (const p of parts) cur = cur?.[p]
    return cur
}

const isLongTextKey = (key: string) =>
    /(text|description|content|html|notice|additional)/i.test(key)

const isFileKey = (key: string) => {
    const k = String(key || '').toLowerCase()
    return (
        k === 'file' ||
        k.endsWith('_file') ||
        k.includes('file_') ||
        k.includes('image') ||
        k.includes('avatar') ||
        k.includes('icon') ||
        k.includes('photo') ||
        k.includes('background')
    )
}

const isFilesArrayObjectShape = (arr: any[]) => {
    if (!Array.isArray(arr)) return false
    if (arr.length === 0) return true
    return arr.every((x) => {
        if (!isPlainObject(x)) return false
        const keys = Object.keys(x)
        if (keys.length !== 1) return false
        return isFileKey(keys[0])
    })
}

const getArrayFileKey = (arr: any[], fallback = 'file') => {
    const firstObj = arr.find((x) => isPlainObject(x))
    if (!firstObj) return fallback
    const key = Object.keys(firstObj).find((k) => isFileKey(k))
    return key || fallback
}

const fetchWebPageFilesSelect = async (opts: {id?: number | string; search?: string}) => {
    const payload: any = {
        context: {block: 'select'},
        select: ['title'],
    }

    const id = opts.id !== undefined && opts.id !== null ? String(opts.id).trim() : ''
    const q = opts.search !== undefined && opts.search !== null ? String(opts.search).trim() : ''

    if (id) payload.id = id
    if (q) payload.search = q

    const response = await api('webPageFiles', 'get', payload)
    return (response?.data ?? []) as TSelectOption[]
}

const FileSelect: React.FC<{
    valueId: any
    disabled?: boolean
    onChangeId: (id: number | null) => void
}> = ({valueId, disabled, onChangeId}) => {
    const [selected, setSelected] = useState<{value: number; label: string} | null>(null)
    const mounted = useRef(false)

    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
        }
    }, [])

    useEffect(() => {
        const idNum = typeof valueId === 'string' ? Number(valueId) : valueId
        if (!idNum || !Number.isFinite(idNum)) {
            setSelected(null)
            return
        }

        ;(async () => {
            const rows = await fetchWebPageFilesSelect({id: idNum})
            const row = rows?.[0]
            if (!mounted.current) return
            setSelected(
                row
                    ? {value: Number(row.value), label: String(row.title)}
                    : {value: Number(idNum), label: `#${idNum}`}
            )
        })()
    }, [valueId])

    const loadOptions = useCallback(async (inputValue: string) => {
        const rows = await fetchWebPageFilesSelect({search: inputValue})
        return (rows || []).map((r) => ({value: Number(r.value), label: String(r.title)}))
    }, [])

    return (
        <AsyncSelect
            cacheOptions
            defaultOptions
            isClearable
            isDisabled={Boolean(disabled)}
            value={selected}
            loadOptions={loadOptions}
            onChange={(opt) => {
                const next = opt as any
                setSelected(next ?? null)
                onChangeId(next?.value ?? null)
            }}
            placeholder='Выберите файл...'
            classNamePrefix='jbSelect'
        />
    )
}

const ComponentJsonBuilder: React.FC<TComponentJsonBuilder> = ({
    article,
    is_disabled,
    defaultJson,
}) => {
    const [{value}, , helpers] = useField<string>(article)

    const [mode, setMode] = useState<'form' | 'json'>('form')
    const [jsonDraft, setJsonDraft] = useState<string>('')
    const [jsonError, setJsonError] = useState<string>('')

    useEffect(() => {
        if (is_disabled) return
        const empty = value === null || value === undefined || String(value).trim() === ''
        if (!empty) return
        const resolvedDefault =
            typeof defaultJson === 'string' && defaultJson.trim() ? defaultJson : '{\n  \n}'
        helpers.setValue(resolvedDefault)
    }, [])

    const parsed = useMemo(() => safeParse(value), [value])

    useEffect(() => {
        setJsonDraft(typeof value === 'string' ? value : pretty(value))
        setJsonError('')
    }, [value])

    const commitObject = useCallback(
        (nextObj: any) => {
            helpers.setValue(pretty(nextObj))
        },
        [helpers]
    )

    const handleChangePath = useCallback(
        (path: string, next: any) => {
            const obj = safeParse(value) ?? {}
            commitObject(setByPath(obj, path, next))
        },
        [commitObject, value]
    )

    const handleArrayAdd = useCallback(
        (path: string) => {
            const obj = safeParse(value) ?? {}
            const arr = (getFromPath(obj, path) ?? []) as any[]
            const first = arr.find((x) => x != null)

            const template = isPlainObject(first)
                ? Object.keys(first).reduce((acc: any, k) => {
                      acc[k] = isFileKey(k) ? null : ''
                      return acc
                  }, {})
                : typeof first === 'string'
                ? ''
                : typeof first === 'number'
                ? 0
                : typeof first === 'boolean'
                ? false
                : ''

            commitObject(setByPath(obj, path, [...arr, template]))
        },
        [commitObject, value]
    )

    const handleArrayRemove = useCallback(
        (path: string, idx: number) => {
            const obj = safeParse(value) ?? {}
            const arr = (getFromPath(obj, path) ?? []) as any[]
            commitObject(
                setByPath(
                    obj,
                    path,
                    arr.filter((_, i) => i !== idx)
                )
            )
        },
        [commitObject, value]
    )

    const applyJsonDraft = useCallback(() => {
        if (is_disabled) return
        const raw = String(jsonDraft ?? '').trim()
        if (!raw) {
            setJsonError('JSON пустой')
            return
        }
        try {
            const obj = JSON.parse(raw)
            setJsonError('')
            helpers.setValue(pretty(obj))
        } catch {
            setJsonError('Некорректный JSON')
        }
    }, [helpers, is_disabled, jsonDraft])

    const renderNode = useCallback(
        (node: any, pathPrefix = '', label?: string): React.ReactNode => {
            const t = guessType(node)

            if (t === 'object') {
                const entries = Object.entries(node as Record<string, any>)
                return (
                    <div className={label ? 'componentJsonBuilder__nest' : ''}>
                        {label ? (
                            <div className='componentJsonBuilder__nestTitle'>{label}</div>
                        ) : null}

                        <div className='componentJsonBuilder__grid'>
                            {entries.map(([key, val]) => {
                                const path = pathPrefix ? `${pathPrefix}.${key}` : key
                                const vt = guessType(val)

                                if (vt === 'array') {
                                    return (
                                        <div key={path} className='componentJsonBuilder__field'>
                                            {renderNode(val, path, key)}
                                        </div>
                                    )
                                }

                                if (vt === 'object') {
                                    return (
                                        <div key={path} className='componentJsonBuilder__field'>
                                            {renderNode(val, path, key)}
                                        </div>
                                    )
                                }

                                if (isFileKey(key)) {
                                    return (
                                        <div key={path} className='componentJsonBuilder__field'>
                                            <label className='componentJsonBuilder__label'>
                                                {key}
                                            </label>
                                            <FileSelect
                                                valueId={val}
                                                disabled={Boolean(is_disabled)}
                                                onChangeId={(id) => handleChangePath(path, id)}
                                            />
                                        </div>
                                    )
                                }

                                if (vt === 'string') {
                                    const useTextarea =
                                        isLongTextKey(key) ||
                                        String(val ?? '').includes('\n') ||
                                        String(val ?? '').length > 80

                                    return (
                                        <div key={path} className='componentJsonBuilder__field'>
                                            <label className='componentJsonBuilder__label'>
                                                {key}
                                            </label>
                                            <Form.Control
                                                as={useTextarea ? 'textarea' : 'input'}
                                                value={val ?? ''}
                                                disabled={Boolean(is_disabled)}
                                                onChange={(e) =>
                                                    handleChangePath(path, e.target.value)
                                                }
                                            />
                                        </div>
                                    )
                                }

                                if (vt === 'number') {
                                    return (
                                        <div key={path} className='componentJsonBuilder__field'>
                                            <label className='componentJsonBuilder__label'>
                                                {key}
                                            </label>
                                            <Form.Control
                                                type='number'
                                                value={Number(val ?? 0)}
                                                disabled={Boolean(is_disabled)}
                                                onChange={(e) =>
                                                    handleChangePath(path, Number(e.target.value))
                                                }
                                            />
                                        </div>
                                    )
                                }

                                if (vt === 'boolean') {
                                    return (
                                        <div key={path} className='componentJsonBuilder__field'>
                                            <label className='componentJsonBuilder__label'>
                                                {key}
                                            </label>
                                            <Form.Check
                                                type='switch'
                                                checked={Boolean(val)}
                                                disabled={Boolean(is_disabled)}
                                                onChange={(e) =>
                                                    handleChangePath(path, e.target.checked)
                                                }
                                                label=''
                                            />
                                        </div>
                                    )
                                }

                                return (
                                    <div key={path} className='componentJsonBuilder__field'>
                                        <label className='componentJsonBuilder__label'>{key}</label>
                                        <div className='text-muted'>—</div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )
            }

            if (t === 'array') {
                const arr = (node as any[]) ?? []
                const isArrayOfObjects = arr.length === 0 ? true : arr.some((x) => isPlainObject(x))

                if (isArrayOfObjects && isFilesArrayObjectShape(arr)) {
                    const fileKey = getArrayFileKey(arr, 'file')

                    return (
                        <div className='componentJsonBuilder__nest'>
                            {label ? (
                                <div className='componentJsonBuilder__arrayItemHeader'>
                                    <div className='componentJsonBuilder__arrayItemTitle'>
                                        {label}
                                    </div>
                                    <div className='componentJsonBuilder__arrayActions'>
                                        <button
                                            type='button'
                                            className='componentJsonBuilder__btn'
                                            disabled={Boolean(is_disabled)}
                                            onClick={() => {
                                                const obj = safeParse(value) ?? {}
                                                const nextArr = [...arr, {[fileKey]: null}]
                                                commitObject(setByPath(obj, pathPrefix, nextArr))
                                            }}
                                        >
                                            + Добавить
                                        </button>
                                    </div>
                                </div>
                            ) : null}

                            {!arr.length ? (
                                <div className='componentJsonBuilder__hint'>Пока пусто</div>
                            ) : null}

                            <div className='componentJsonBuilder__filesList'>
                                {arr.map((item, idx) => {
                                    const itemPath = `${pathPrefix}[${idx}].${fileKey}`
                                    const currentId = (item as any)?.[fileKey]

                                    return (
                                        <div
                                            key={itemPath}
                                            className='componentJsonBuilder__filesRow'
                                        >
                                            <div className='componentJsonBuilder__filesSelect'>
                                                <FileSelect
                                                    valueId={currentId}
                                                    disabled={Boolean(is_disabled)}
                                                    onChangeId={(id) =>
                                                        handleChangePath(itemPath, id)
                                                    }
                                                />
                                            </div>

                                            <button
                                                type='button'
                                                className='componentJsonBuilder__btn componentJsonBuilder__btnDanger'
                                                disabled={Boolean(is_disabled)}
                                                onClick={() => handleArrayRemove(pathPrefix, idx)}
                                            >
                                                Удалить
                                            </button>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )
                }

                const isArrayOfObjectsStrict = arr.some((x) => isPlainObject(x))

                return (
                    <div className='componentJsonBuilder__nest'>
                        {label ? (
                            <div className='componentJsonBuilder__arrayItemHeader'>
                                <div className='componentJsonBuilder__arrayItemTitle'>{label}</div>
                                <div className='componentJsonBuilder__arrayActions'>
                                    <button
                                        type='button'
                                        className='componentJsonBuilder__btn'
                                        disabled={Boolean(is_disabled)}
                                        onClick={() => handleArrayAdd(pathPrefix)}
                                    >
                                        + Добавить
                                    </button>
                                </div>
                            </div>
                        ) : null}

                        {arr.length === 0 ? (
                            <div className='componentJsonBuilder__hint'>Пока пусто</div>
                        ) : isArrayOfObjectsStrict ? (
                            <div className='componentJsonBuilder__arrayList'>
                                {arr.map((item, idx) => {
                                    const itemPath = `${pathPrefix}[${idx}]`
                                    return (
                                        <div
                                            key={itemPath}
                                            className='componentJsonBuilder__arrayItem'
                                        >
                                            <div className='componentJsonBuilder__arrayItemHeader'>
                                                <div className='componentJsonBuilder__arrayItemTitle'>
                                                    Элемент #{idx + 1}
                                                </div>
                                                <div className='componentJsonBuilder__arrayActions'>
                                                    <button
                                                        type='button'
                                                        className='componentJsonBuilder__btn componentJsonBuilder__btnDanger'
                                                        disabled={Boolean(is_disabled)}
                                                        onClick={() =>
                                                            handleArrayRemove(pathPrefix, idx)
                                                        }
                                                    >
                                                        Удалить
                                                    </button>
                                                </div>
                                            </div>
                                            {renderNode(item ?? {}, itemPath)}
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (
                            <div className='componentJsonBuilder__arrayList'>
                                {arr.map((item, idx) => {
                                    const itemPath = `${pathPrefix}[${idx}]`
                                    const it = guessType(item)

                                    return (
                                        <div
                                            key={itemPath}
                                            className='componentJsonBuilder__arrayItem'
                                        >
                                            <div className='componentJsonBuilder__arrayItemHeader'>
                                                <div className='componentJsonBuilder__arrayItemTitle'>
                                                    Элемент #{idx + 1}
                                                </div>
                                                <div className='componentJsonBuilder__arrayActions'>
                                                    <button
                                                        type='button'
                                                        className='componentJsonBuilder__btn componentJsonBuilder__btnDanger'
                                                        disabled={Boolean(is_disabled)}
                                                        onClick={() =>
                                                            handleArrayRemove(pathPrefix, idx)
                                                        }
                                                    >
                                                        Удалить
                                                    </button>
                                                </div>
                                            </div>

                                            {it === 'string' ? (
                                                <Form.Control
                                                    value={String(item ?? '')}
                                                    disabled={Boolean(is_disabled)}
                                                    onChange={(e) =>
                                                        handleChangePath(itemPath, e.target.value)
                                                    }
                                                />
                                            ) : it === 'number' ? (
                                                <Form.Control
                                                    type='number'
                                                    value={Number(item ?? 0)}
                                                    disabled={Boolean(is_disabled)}
                                                    onChange={(e) =>
                                                        handleChangePath(
                                                            itemPath,
                                                            Number(e.target.value)
                                                        )
                                                    }
                                                />
                                            ) : it === 'boolean' ? (
                                                <Form.Check
                                                    type='switch'
                                                    checked={Boolean(item)}
                                                    disabled={Boolean(is_disabled)}
                                                    onChange={(e) =>
                                                        handleChangePath(itemPath, e.target.checked)
                                                    }
                                                    label=''
                                                />
                                            ) : (
                                                <div className='text-muted'>—</div>
                                            )}
                                        </div>
                                    )
                                })}

                                <button
                                    type='button'
                                    className='componentJsonBuilder__btn'
                                    disabled={Boolean(is_disabled)}
                                    onClick={() => handleArrayAdd(pathPrefix)}
                                >
                                    + Добавить
                                </button>
                            </div>
                        )}
                    </div>
                )
            }

            return null
        },
        [commitObject, handleArrayAdd, handleArrayRemove, handleChangePath, is_disabled, value]
    )

    return (
        <div className='componentJsonBuilder'>
            <Nav
                variant='tabs'
                className='componentJsonBuilder__tabs'
                activeKey={mode}
                onSelect={(k) => setMode((k as any) || 'form')}
            >
                <Nav.Item>
                    <Nav.Link eventKey='form'>Конструктор</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey='json'>JSON</Nav.Link>
                </Nav.Item>
            </Nav>

            {mode === 'form' ? (
                <div className='componentJsonBuilder__panel'>
                    {parsed ? (
                        renderNode(parsed, '')
                    ) : (
                        <div className='componentJsonBuilder__broken'>
                            <div className='componentJsonBuilder__brokenTitle'>
                                JSON некорректный
                            </div>
                            <div className='componentJsonBuilder__brokenText'>
                                Перейди во вкладку JSON и исправь.
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className='componentJsonBuilder__panel'>
                    <Form.Control
                        as='textarea'
                        className='componentJsonBuilder__code'
                        value={jsonDraft}
                        disabled={Boolean(is_disabled)}
                        onChange={(e) => setJsonDraft(e.target.value)}
                        onBlur={applyJsonDraft}
                    />
                    {jsonError ? (
                        <div className='componentJsonBuilder__error'>{jsonError}</div>
                    ) : null}
                </div>
            )}
        </div>
    )
}

export default ComponentJsonBuilder
